import { IBEError } from '@common/interfaces/be-error.interface';
import { ICollection } from '@common/interfaces/collection.interface';
import { UseQueryOptions } from '@common/interfaces/query-options.interface';
import { createCollection } from '@common/utils/create-collection.util';
import { customNotify } from '@common/utils/custom-notify.util';
import { createBoxCustomisationConfigDto, IBoxCustomisationConfigDto } from '@src/api/model/box-customisation-config.dto';
import {
    packHouseApiRequest,
    TPackhouseV1BoxCustomisationConfigByDeliveryIdIdGetError,
} from '@src/api/pack-house-generated-api';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { apiHooks } from '../api-hooks';

const NAME = 'box-customisation-config';

const useGetBoxCustomisationConfigByDeliveryConfigId = (
    deliveryConfigId: string,
    delivery_date: number,
    queryOptions: UseQueryOptions,
): UseQueryResult<
    ICollection<IBoxCustomisationConfigDto>,
    IBEError<TPackhouseV1BoxCustomisationConfigByDeliveryIdIdGetError>
> => {
    const {
        error: productError,
        isFetched: productFetched,
        isLoading: productLoading,
        remove: productRemove,
    } = apiHooks.packHouse.useGetProducts();

    const {
        error: produceError,
        isFetched: produceFetched,
        isLoading: produceLoading,
        remove: produceRemove,
    } = apiHooks.packHouse.useGetBoxCustomisationProduceList({ enabled: true });

    useEffect(() => {
        customNotify.error([{ beError: productError, onShown: productRemove }]);
        customNotify.error([{ beError: produceError, onShown: produceRemove }]);
    }, [produceError, produceRemove, productError, productRemove]);

    const result = useQuery<
        ICollection<IBoxCustomisationConfigDto>,
        IBEError<TPackhouseV1BoxCustomisationConfigByDeliveryIdIdGetError>
    >(
        [`${NAME}-${deliveryConfigId}-list-by-delivery`, delivery_date],
        () => {
            return packHouseApiRequest
                .packhouseV1BoxCustomisationConfigByDeliveryIdIdGet(deliveryConfigId, {
                    delivery_date: dayjs(delivery_date).endOf('day').unix(),
                })
                .then((r) => createCollection(r.data.list?.map((e) => createBoxCustomisationConfigDto(e)) || [], 0, {}));
        },
        {
            ...queryOptions,
            enabled: productFetched && produceFetched && queryOptions?.enabled,
        },
    );

    const isLoading = result.isLoading || productLoading || produceLoading;
    return { ...result, isFetching: isLoading };
};

export const boxCustomisationConfig = {
    useGetBoxCustomisationConfigByDeliveryConfigId,
};
